/** Cells every report's table shows the same way: a day, a ticket, a status, a ticket's state. */
import { Link } from "react-router";

import type { ReportStatus, TicketState } from "../../api/types";
import { Badge } from "../../components/ui/Badge";
import { formatLongDay, formatShortDay } from "../../lib/dates";
import { possessive } from "../../lib/possessive";
import { STATUS_TONES, TICKET_STATE_LABELS, TICKET_STATE_TONES } from "./common";

/** The tech's local day, opening their day view. */
export function DayLink({ day, userId, tech }: { day: string; userId: string; tech: string }) {
  return (
    <Link
      to={`/?${new URLSearchParams({ date: day, user: userId }).toString()}`}
      title={`Open ${possessive(tech)} day, ${formatLongDay(day)}`}
      className="font-medium text-brand-ink hover:underline"
    >
      {formatShortDay(day)}
      <span className="sr-only">{`: open ${possessive(tech)} day`}</span>
    </Link>
  );
}

/** A ticket by its number; only its link to Autotask has the ID. */
export function TicketLink({ number, url }: { number: string | null; url: string | null }) {
  if (number === null || url === null) {
    return number;
  }
  // Autotask's own ExecuteCommand link (ADR 0066), named by the ticket's number.
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold text-brand-ink underline underline-offset-2"
    >
      {number}
    </a>
  );
}

/** A block's status in the day view's words. */
export function StatusBadge({ status, label }: { status: ReportStatus; label: string }) {
  return <Badge tone={STATUS_TONES[status]}>{label}</Badge>;
}

/** Whether a ticket is still open, as Axon Time knows it (ADRs 0181, 0185). */
export function TicketStateBadge({ state }: { state: TicketState }) {
  return <Badge tone={TICKET_STATE_TONES[state]}>{TICKET_STATE_LABELS[state]}</Badge>;
}

/** A row's ticket by its number, and under it whether that ticket is still open. */
export function TicketCell({
  number,
  url,
  state,
}: {
  number: string | null;
  url: string | null;
  state: TicketState | null;
}) {
  if (state === null) {
    return <TicketLink number={number} url={url} />;
  }
  return (
    <span className="flex flex-col gap-1">
      <TicketLink number={number} url={url} />
      <TicketStateBadge state={state} />
    </span>
  );
}
